import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen, GraduationCap, Briefcase, Rocket, Star, Target,
  Code, Database, Cloud, Lock, Zap, TrendingUp, Users, Settings,
  BarChart, FileText, Globe, Shield, Cpu, Layers, Package, Award,
  Calendar, CheckCircle, Compass, DollarSign, Eye, Film, Gift,
  ArrowLeft, Book, Plus, RefreshCw, AlertCircle, WifiOff, X, Pencil, Trash2,
} from 'lucide-react';
import CourseViewer from './courseviewer';

// ─────────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────────
const BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app/isfathena';

const MODAL_ENDPOINTS = [
  `${BASE_URL}/modal/list`,
  `${BASE_URL}/modals/list`,
  `${BASE_URL}/models/list`,
];

const PRODUCT_ENDPOINT = `${BASE_URL}/product/list`;

const GRADIENTS = [
  'from-orange-500 via-pink-500 to-purple-600',
  'from-blue-500 via-cyan-500 to-teal-600',
  'from-green-500 via-emerald-500 to-teal-600',
  'from-purple-500 via-pink-500 to-red-600',
  'from-yellow-500 via-orange-500 to-red-600',
  'from-indigo-500 via-purple-500 to-pink-600',
];

// ─────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────
const extractModalsArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const knownKeys = ['modals', 'modal', 'models', 'data', 'list', 'result', 'results', 'items', 'records'];
  for (const key of knownKeys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return Object.values(data).find((v) => Array.isArray(v)) || [];
};

const fetchWithTimeout = async (url, timeout = 15000) => {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeout);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    clearTimeout(id);
    return res;
  } catch (err) {
    clearTimeout(id);
    if (err.name === 'AbortError') throw new Error(`Request timed out after ${timeout / 1000}s`);
    throw err;
  }
};

// ─────────────────────────────────────────────────────────────
//  ICON SELECTORS
// ─────────────────────────────────────────────────────────────
const ALL_ICONS = [
  BookOpen, GraduationCap, Briefcase, Rocket, Star, Target,
  Code, Database, Cloud, Lock, Zap, TrendingUp, Users, Settings,
  BarChart, FileText, Globe, Shield, Cpu, Layers, Package, Award,
  Calendar, CheckCircle, Compass, DollarSign, Eye, Film, Gift,
];

const getIconForProduct = (name = '', idx) => {
  const n = name.toLowerCase();
  if (n.includes('lms') || n.includes('learning')) return BookOpen;
  if (n.includes('tutor')) return GraduationCap;
  if (n.includes('academy')) return Award;
  if (n.includes('skill')) return Target;
  if (n.includes('star')) return Star;
  if (n.includes('combined')) return Layers;
  if (n.includes('course') || n.includes('learn') || n.includes('edu')) return BookOpen;
  if (n.includes('degree') || n.includes('student')) return GraduationCap;
  if (n.includes('business') || n.includes('enterprise')) return Briefcase;
  return ALL_ICONS[idx % ALL_ICONS.length];
};

const MODEL_ICONS = [BookOpen, FileText, Book, GraduationCap, Award, Target, Code, Database, Cloud, Lock, Zap, TrendingUp];
const getIconForModel = (name = '', idx) => {
  const n = name.toLowerCase();
  if (n.includes('course') || n.includes('curriculum')) return BookOpen;
  if (n.includes('exam') || n.includes('test') || n.includes('quiz')) return FileText;
  if (n.includes('book') || n.includes('text')) return Book;
  if (n.includes('tutorial') || n.includes('lesson')) return GraduationCap;
  if (n.includes('certificate')) return Award;
  if (n.includes('skill')) return Target;
  return MODEL_ICONS[idx % MODEL_ICONS.length];
};

// ─────────────────────────────────────────────────────────────
//  REUSABLE UI COMPONENTS
// ─────────────────────────────────────────────────────────────
const Spinner = ({ text = 'Loading…' }) => (
  <div className="flex flex-col items-center gap-4 py-20">
    <div className="relative w-16 h-16">
      <div className="absolute inset-0 border-4 border-gray-700 rounded-full" />
      <div className="absolute inset-0 border-4 border-t-orange-500 rounded-full animate-spin" />
    </div>
    <p className="text-gray-400 text-lg">{text}</p>
  </div>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-6 flex flex-col sm:flex-row items-center gap-4">
    <AlertCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
    <p className="text-red-300 text-sm flex-1">{message}</p>
    {onRetry && (
      <button onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 text-red-300 rounded-lg text-sm font-semibold hover:bg-red-500/30 transition-all flex-shrink-0">
        <RefreshCw className="w-4 h-4" /> Retry
      </button>
    )}
  </div>
);

const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <div className="w-20 h-20 bg-red-500/20 border border-red-500/40 rounded-full flex items-center justify-center mx-auto mb-6">
        <Shield className="w-10 h-10 text-red-400" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-3">Access Restricted</h1>
      <p className="text-gray-400 mb-6">
        You do not have permission to view the Reference page.
        Please contact your administrator to request access.
      </p>
      <button
        onClick={() => (window.location.href = '/')}
        className="px-6 py-3 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-all">
        Go to Home
      </button>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
//  PRODUCT MODAL  (Add / Edit)
// ─────────────────────────────────────────────────────────────
const ProductFormModal = ({ mode, product, onClose, onSaved }) => {
  const [name, setName]       = useState(product?.product || '');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const isEdit = mode === 'edit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError('Product name is required.'); return; }

    setLoading(true);
    setError('');

    try {
      const fd = new FormData();
      let url;

      if (isEdit) {
        fd.append('id', product.id);
        fd.append('product', trimmed);
        url = `${BASE_URL}/product/update`;
      } else {
        fd.append('product', trimmed);
        url = `${BASE_URL}/product/create`;
      }

      const res  = await fetch(url, { method: 'POST', body: fd });
      const data = await res.json();

      if (data.success) {
        onSaved();
      } else {
        setError(data.message || `Failed to ${isEdit ? 'update' : 'create'} product.`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close on backdrop click
  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      onClick={handleBackdrop}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg">
            {isEdit ? '✏️ Edit Product' : '➕ Add New Product'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-1.5">
              Product Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="e.g. EasyLearn LMS"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-500
                         focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30 transition-all"
              autoFocus
            />
            {error && (
              <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {error}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-white/20 text-gray-300 rounded-xl text-sm font-semibold
                         hover:bg-white/10 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl
                         text-sm font-bold hover:from-orange-600 hover:to-orange-700 transition-all
                         disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {isEdit ? 'Saving…' : 'Adding…'}
                </>
              ) : isEdit ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
//  DELETE CONFIRM DIALOG  (for both products and modals)
// ─────────────────────────────────────────────────────────────
const DeleteConfirmDialog = ({ target, onCancel, onConfirm, loading, error }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
    <div className="bg-gray-900 border border-red-500/50 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
      <div className="flex justify-center mb-4">
        <div className="w-14 h-14 bg-red-500/20 rounded-full flex items-center justify-center">
          <Trash2 className="w-7 h-7 text-red-400" />
        </div>
      </div>
      <h3 className="text-white font-bold text-lg text-center mb-2">
        Delete {target.type === 'product' ? 'Product' : 'Modal'}?
      </h3>
      <p className="text-gray-400 text-sm text-center mb-5">
        Are you sure you want to permanently delete{' '}
        <span className="text-white font-semibold">"{target.name}"</span>?
        {target.type === 'product' && (
          <span className="block mt-1 text-yellow-400/80 text-xs">
            ⚠️ This may also remove associated modals.
          </span>
        )}
      </p>
      {error && <p className="text-red-400 text-xs text-center mb-3">{error}</p>}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2.5 border border-white/20 text-gray-300 rounded-xl text-sm font-semibold
                     hover:bg-white/10 transition-all disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-sm font-bold
                     hover:from-red-700 hover:to-red-800 transition-all disabled:opacity-50
                     flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Deleting…
            </>
          ) : 'Yes, Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
function Reference() {
  const [searchParams] = useSearchParams();

  // ── Views ───────────────────────────────────────────────────
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedModel,   setSelectedModel]   = useState(null);

  // ── Data ────────────────────────────────────────────────────
  const [products,       setProducts]       = useState([]);
  const [models,         setModels]         = useState([]);

  // ── Loading / Error ─────────────────────────────────────────
  const [productsLoading, setProductsLoading] = useState(true);
  const [modelsLoading,   setModelsLoading]   = useState(false);
  const [productsError,   setProductsError]   = useState(null);
  const [modelsError,     setModelsError]     = useState(null);

  // ── Auth ────────────────────────────────────────────────────
  const [isSuperAdmin,  setIsSuperAdmin]  = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess,     setHasAccess]     = useState(false);

  // ── Product CRUD modal state ─────────────────────────────────
  const [productFormMode,   setProductFormMode]   = useState(null); // 'add' | 'edit' | null
  const [editingProduct,    setEditingProduct]    = useState(null);
  const [productActionMsg,  setProductActionMsg]  = useState('');   // success flash

  // ── Delete confirm state (shared for both products & modals) ─
  const [deleteTarget,  setDeleteTarget]  = useState(null);  // { id, name, type: 'product'|'modal' }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [deleteError,   setDeleteError]   = useState('');

  // ────────────────────────────────────────────────────────────
  //  AUTH CHECK
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) { window.location.href = '/login'; return; }

      const user = JSON.parse(raw);

      if (user.role === 'super_admin' || user.email === 'info@easylearn.net.in') {
        setIsSuperAdmin(true);
        setHasAccess(true);
        setAccessChecked(true);
        localStorage.setItem('refAccess', '1');
        window.dispatchEvent(new Event('refAccessChange'));
        return;
      }

      fetch(`${BASE_URL}/user/can-access-reference?email=${encodeURIComponent(user.email)}`)
        .then((r) => r.json())
        .then((data) => {
          const canAccess = Boolean(data.can_access);
          setHasAccess(canAccess);
          setAccessChecked(true);
          localStorage.setItem('refAccess', canAccess ? '1' : '0');
          window.dispatchEvent(new Event('refAccessChange'));
        })
        .catch(() => {
          setHasAccess(false);
          setAccessChecked(true);
          localStorage.setItem('refAccess', '0');
          window.dispatchEvent(new Event('refAccessChange'));
        });
    } catch (e) {
      setHasAccess(false);
      setAccessChecked(true);
    }
  }, []);

  // ────────────────────────────────────────────────────────────
  //  FETCH MODELS
  // ────────────────────────────────────────────────────────────
  const fetchModelsForProduct = useCallback(async (productId) => {
    setModelsLoading(true);
    setModelsError(null);
    setModels([]);
    let lastError = null;

    for (const endpoint of MODAL_ENDPOINTS) {
      try {
        const res = await fetchWithTimeout(endpoint);
        if (!res.ok) { lastError = new Error(`${res.status} ${res.statusText}`); continue; }
        const data = await res.json();
        const allModals = extractModalsArray(data);
        const filtered  = allModals.filter((m) => String(m.product_id) === String(productId));
        setModels(filtered);
        if (!filtered.length) {
          setModelsError(
            `No models found for this product (product_id = ${productId}). ` +
            `Total modals in system: ${allModals.length}. ` +
            `Please add models via the admin panel.`
          );
        }
        setModelsLoading(false);
        return;
      } catch (err) {
        lastError = err;
      }
    }

    setModelsError(
      lastError?.message?.includes('timed out')
        ? 'Request timed out — check your internet connection.'
        : `API Error: ${lastError?.message || 'Unknown error'}`
    );
    setModelsLoading(false);
  }, []);

  // ────────────────────────────────────────────────────────────
  //  FETCH PRODUCTS
  // ────────────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const res  = await fetchWithTimeout(PRODUCT_ENDPOINT);
      if (!res.ok) throw new Error(`Server returned ${res.status} ${res.statusText}`);
      const data = await res.json();

      const list = Array.isArray(data)
        ? data
        : (data.products || data.data || data.list || data.result || data.results || []);

      setProducts(list);

      const productIdFromUrl = searchParams.get('productId');
      if (productIdFromUrl) {
        const matched = list.find((p) => String(p.id) === String(productIdFromUrl));
        if (matched) {
          setProductsLoading(false);
          setSelectedProduct(matched);
          setSelectedModel(null);
          setModels([]);
          setModelsError(null);
          fetchModelsForProduct(matched.id);
          return;
        }
      }
    } catch (err) {
      setProductsError(
        err.message.includes('timed out')
          ? 'Request timed out. Please check your connection and try again.'
          : `Failed to load products: ${err.message}`
      );
    } finally {
      setProductsLoading(false);
    }
  }, [searchParams, fetchModelsForProduct]);

  useEffect(() => {
    if (accessChecked && hasAccess) fetchProducts();
  }, [accessChecked, hasAccess, fetchProducts]);

  // ────────────────────────────────────────────────────────────
  //  NAVIGATION HANDLERS
  // ────────────────────────────────────────────────────────────
  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    setSelectedModel(null);
    setModels([]);
    setModelsError(null);
    fetchModelsForProduct(product.id);
  }, [fetchModelsForProduct]);

  const handleModelClick   = useCallback((model) => setSelectedModel(model), []);
  const handleBackToModels = useCallback(() => setSelectedModel(null), []);
  const handleBackToProducts = useCallback(() => {
    setSelectedProduct(null);
    setSelectedModel(null);
    setModels([]);
    setModelsError(null);
  }, []);

  const handleNavigateToAddModal = useCallback((productId = null, productName = null) => {
    if (productId && productName) {
      window.location.href = `/addmodal?productId=${productId}&productName=${encodeURIComponent(productName)}`;
    } else {
      window.location.href = '/addmodal';
    }
  }, []);

  // ────────────────────────────────────────────────────────────
  //  PRODUCT CRUD HANDLERS
  // ────────────────────────────────────────────────────────────
  const handleOpenAddProduct = useCallback(() => {
    setEditingProduct(null);
    setProductFormMode('add');
  }, []);

  const handleOpenEditProduct = useCallback((e, product) => {
    e.stopPropagation();
    setEditingProduct(product);
    setProductFormMode('edit');
  }, []);

  const handleProductSaved = useCallback(async () => {
    setProductFormMode(null);
    setEditingProduct(null);
    const msg = productFormMode === 'edit'
      ? '✅ Product updated successfully.'
      : '✅ Product added successfully.';
    setProductActionMsg(msg);
    await fetchProducts();
    setTimeout(() => setProductActionMsg(''), 4000);
  }, [fetchProducts, productFormMode]);

  const handleDeleteProductClick = useCallback((e, product) => {
    e.stopPropagation();
    setDeleteError('');
    setDeleteSuccess('');
    setDeleteTarget({ id: product.id, name: product.product, type: 'product' });
  }, []);

  // ── Delete modal handler ─────────────────────────────────────
  const handleEditModal   = useCallback((e, model) => {
    e.stopPropagation();
    window.location.href = `/addmodal?editId=${model.id}`;
  }, []);

  const handleDeleteModalClick = useCallback((e, model) => {
    e.stopPropagation();
    setDeleteError('');
    setDeleteSuccess('');
    setDeleteTarget({ id: model.id, name: model.modal_name, type: 'modal' });
  }, []);

  // ── Unified Delete Confirm ───────────────────────────────────
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError('');

    try {
      const fd = new FormData();
      fd.append('id', deleteTarget.id);

      const url = deleteTarget.type === 'product'
        ? `${BASE_URL}/product/delete`
        : `${BASE_URL}/modal/delete`;

      const res  = await fetch(url, { method: 'POST', body: fd });
      const data = await res.json();

      if (data.success) {
        const msg = `✅ "${deleteTarget.name}" deleted successfully.`;
        setDeleteSuccess(msg);
        setDeleteTarget(null);
        // Refresh relevant list
        if (deleteTarget.type === 'product') {
          await fetchProducts();
        } else if (selectedProduct) {
          fetchModelsForProduct(selectedProduct.id);
        }
        setTimeout(() => setDeleteSuccess(''), 4000);
      } else {
        setDeleteError(data.message || 'Delete failed. Please try again.');
      }
    } catch (err) {
      setDeleteError('Network error. Could not delete.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, fetchProducts, selectedProduct, fetchModelsForProduct]);

  // ────────────────────────────────────────────────────────────
  //  EARLY RETURNS
  // ────────────────────────────────────────────────────────────
  if (!accessChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <Spinner text="Checking access…" />
      </div>
    );
  }

  if (!hasAccess) return <AccessDenied />;

  if (selectedModel) {
    return (
      <CourseViewer
        modelData={selectedModel}
        onBack={handleBackToModels}
        showSidebar
        showBottomControls={false}
        showHeader={false}
        siblingsModals={models}
        onSelectModel={(modal) => setSelectedModel(modal)}
        productName={selectedProduct?.product || selectedProduct?.name || ''}
      />
    );
  }

  // ────────────────────────────────────────────────────────────
  //  VIEW: Models list (inside a product)
  // ────────────────────────────────────────────────────────────
  if (selectedProduct) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-20 overflow-x-hidden w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <button
              onClick={handleBackToProducts}
              className="flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors group">
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Products</span>
            </button>

            {isSuperAdmin && (
              <button
                onClick={() => handleNavigateToAddModal(selectedProduct.id, selectedProduct.product)}
                className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600
                           text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700
                           transition-all duration-300 sm:hover:scale-105 shadow-orange-500/30 shadow-lg text-sm sm:text-base">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                Add Document
              </button>
            )}
          </div>

          {/* Loading */}
          {modelsLoading && <Spinner text="Loading models…" />}

          {/* Success / Error banners */}
          {deleteSuccess && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-green-300 text-sm font-medium">{deleteSuccess}</p>
            </div>
          )}
          {deleteError && !deleteTarget && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-300 text-sm font-medium flex-1">{deleteError}</p>
              <button onClick={() => setDeleteError('')} className="text-red-400 hover:text-red-300 text-lg leading-none">×</button>
            </div>
          )}

          {/* Delete confirm dialog */}
          {deleteTarget && (
            <DeleteConfirmDialog
              target={deleteTarget}
              loading={deleteLoading}
              error={deleteError}
              onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
              onConfirm={handleDeleteConfirm}
            />
          )}

          {/* Error */}
          {!modelsLoading && modelsError && (
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-xl p-6 flex flex-col sm:flex-row items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-yellow-300 font-semibold mb-1">No models found</p>
                  <p className="text-yellow-400/70 text-sm whitespace-pre-line">{modelsError}</p>
                </div>
                <button
                  onClick={() => fetchModelsForProduct(selectedProduct.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 rounded-lg text-sm font-semibold hover:bg-yellow-500/30 transition-all flex-shrink-0">
                  <RefreshCw className="w-4 h-4" /> Retry
                </button>
              </div>
              {isSuperAdmin && (
                <div className="text-center">
                  <button
                    onClick={() => handleNavigateToAddModal(selectedProduct.id, selectedProduct.product)}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300">
                    + Add Document
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Models grid — grouped by section */}
          {!modelsLoading && models.length > 0 && (() => {
            const grouped = {};
            models.forEach((model) => {
              const key = model.section?.trim() || '';
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(model);
            });

            const sectionKeys = Object.keys(grouped).sort((a, b) => {
              if (a === '' && b !== '') return 1;
              if (a !== '' && b === '') return -1;
              return a.localeCompare(b);
            });

            let globalIdx = 0;
            return sectionKeys.map((sectionKey) => (
              <div key={sectionKey} className="mb-10">
                {sectionKey && (
                  <div className="flex items-center gap-3 sm:gap-4 mb-5 min-w-0">
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                      <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full" />
                      <h2 className="text-lg sm:text-xl font-bold text-white tracking-wide">{sectionKey}</h2>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-orange-500/40 to-transparent min-w-0" />
                    <span className="text-xs text-gray-500 font-medium flex-shrink-0">
                      {grouped[sectionKey].length} {grouped[sectionKey].length === 1 ? 'module' : 'modules'}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {grouped[sectionKey].map((model) => {
                    const index    = globalIdx++;
                    const gradient = GRADIENTS[index % GRADIENTS.length];
                    const Icon     = getIconForModel(model.modal_name, index);
                    const docTypes = ['document', 'book', 'reference_book', 'ppt', 'website', 'social_media'];
                    const availDocs = docTypes.filter((t) => {
                      const u = model[t];
                      return u && u.trim() !== '' && u !== 'null' && u !== 'undefined';
                    }).length;

                    return (
                      <div
                        key={model.id}
                        onClick={() => handleModelClick(model)}
                        className="group bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden
                                   hover:border-orange-500 transition-all duration-300 cursor-pointer
                                   sm:hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30">

                        <div className={`h-32 bg-gradient-to-br ${gradient} flex items-center justify-center relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="relative z-10 text-center">
                            <Icon className="w-12 h-12 text-white drop-shadow-lg mx-auto" strokeWidth={1.5} />
                          </div>
                          {availDocs > 0 && (
                            <div className="absolute top-3 left-3 bg-white/20 backdrop-blur-md px-2 py-1 rounded-full text-white text-xs font-semibold">
                              {availDocs} {availDocs === 1 ? 'Doc' : 'Docs'}
                            </div>
                          )}

                          {/* Superadmin Edit/Delete on modal card */}
                          {isSuperAdmin ? (
                            <div className="absolute top-2 right-2 flex gap-1.5 z-20">
                              <button
                                onClick={(e) => handleEditModal(e, model)}
                                title="Edit modal"
                                className="flex items-center justify-center w-8 h-8 bg-blue-600/90 hover:bg-blue-500 text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-110">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => handleDeleteModalClick(e, model)}
                                title="Delete modal"
                                className="flex items-center justify-center w-8 h-8 bg-red-600/90 hover:bg-red-500 text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-110">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md px-2 py-1 rounded-full text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                              View
                            </div>
                          )}
                        </div>

                        <div className="p-4">
                          <h3 className="text-lg font-bold text-white mb-1 line-clamp-2 group-hover:text-orange-400 transition-colors">
                            {model.modal_name || 'Unnamed Model'}
                          </h3>
                          {model.description && (
                            <p className="text-gray-400 text-xs mb-2 line-clamp-2">{model.description}</p>
                          )}
                          <p className="text-gray-600 text-xs mb-3">ID: {model.id}</p>
                          {availDocs === 0 && (
                            <div className="flex items-center gap-1 text-yellow-500/70 text-xs mb-2">
                              <AlertCircle className="w-3 h-3" />
                              <span>No files uploaded yet</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                            <span className="text-gray-500 text-xs">
                              {model.created_at ? new Date(model.created_at).toLocaleDateString() : 'N/A'}
                            </span>
                            <div className="flex items-center gap-1 text-orange-500 font-semibold text-xs group-hover:gap-2 transition-all">
                              Open <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  //  VIEW: Products list (default / home)
  // ────────────────────────────────────────────────────────────
  if (productsLoading && searchParams.get('productId')) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-20 flex items-center justify-center">
        <Spinner text="Loading…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black pt-20 overflow-x-hidden w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* ── Page Header ─────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Products</h1>
            <p className="text-gray-500 text-sm mt-1">
              {products.length} product{products.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* ADD PRODUCT — Super Admin Only */}
          {isSuperAdmin && (
            <button
              onClick={handleOpenAddProduct}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3
                         bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold
                         rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300
                         sm:hover:scale-105 shadow-lg shadow-orange-500/30 text-sm sm:text-base">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              Add Product
            </button>
          )}
        </div>

        {/* ── Success flash message ────────────────────────────── */}
        {(productActionMsg || deleteSuccess) && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm font-medium">{productActionMsg || deleteSuccess}</p>
          </div>
        )}

        {/* ── Error flash ─────────────────────────────────────── */}
        {deleteError && !deleteTarget && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm font-medium flex-1">{deleteError}</p>
            <button onClick={() => setDeleteError('')} className="text-red-400 hover:text-red-300 text-lg">×</button>
          </div>
        )}

        {/* ── Loading ──────────────────────────────────────────── */}
        {productsLoading && <Spinner text="Loading products…" />}

        {/* ── Fetch Error ──────────────────────────────────────── */}
        {!productsLoading && productsError && (
          <ErrorBanner message={productsError} onRetry={fetchProducts} />
        )}

        {/* ── Empty State ──────────────────────────────────────── */}
        {!productsLoading && !productsError && products.length === 0 && (
          <div className="text-center py-20">
            <WifiOff className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg font-semibold">No products found</p>
            <p className="text-gray-600 text-sm mt-2">The product list is empty or the API returned no data.</p>
            <button
              onClick={fetchProducts}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 transition-all mx-auto">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        )}

        {/* ── Products Grid ────────────────────────────────────── */}
        {!productsLoading && !productsError && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {products.map((product, index) => {
              const gradient = GRADIENTS[index % GRADIENTS.length];
              const Icon     = getIconForProduct(product.product, index);

              return (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="group relative bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl
                             overflow-hidden hover:border-orange-500 transition-all duration-300 cursor-pointer
                             sm:hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/30">

                  {/* Card Image / Gradient */}
                  <div className={`h-32 bg-gradient-to-br ${gradient} flex items-center justify-center relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                    {/* Product Icon */}
                    <div className="relative z-10">
                      <Icon className="w-12 h-12 text-white drop-shadow-lg" strokeWidth={1.5} />
                    </div>

                    {/* "Open" badge — normal users on hover */}
                    {!isSuperAdmin && (
                      <div className="absolute top-3 right-3 bg-white/20 backdrop-blur-md px-2 py-1 rounded-full
                                      text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                        Open
                      </div>
                    )}

                    {/* ── SUPER ADMIN: Edit & Delete Buttons ── */}
                    {isSuperAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1.5 z-20">
                        {/* Edit */}
                        <button
                          onClick={(e) => handleOpenEditProduct(e, product)}
                          title="Edit product"
                          className="flex items-center justify-center w-8 h-8 bg-blue-600/90 hover:bg-blue-500
                                     text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-110">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={(e) => handleDeleteProductClick(e, product)}
                          title="Delete product"
                          className="flex items-center justify-center w-8 h-8 bg-red-600/90 hover:bg-red-500
                                     text-white rounded-lg shadow-lg transition-all duration-200 hover:scale-110">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-orange-400 transition-colors">
                      {product.product}
                    </h3>
                    <p className="text-gray-500 text-xs mb-3">ID: {product.id}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                      <span className="text-gray-500 text-xs">
                        {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'}
                      </span>
                      <div className="flex items-center gap-1 text-orange-500 font-semibold text-xs group-hover:gap-2 transition-all">
                        Open <span className="group-hover:translate-x-1 transition-transform inline-block">→</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── PRODUCT FORM MODAL (Add / Edit) ───────────────────── */}
      {productFormMode && (
        <ProductFormModal
          mode={productFormMode}
          product={editingProduct}
          onClose={() => { setProductFormMode(null); setEditingProduct(null); }}
          onSaved={handleProductSaved}
        />
      )}

      {/* ── DELETE CONFIRM DIALOG ─────────────────────────────── */}
      {deleteTarget && (
        <DeleteConfirmDialog
          target={deleteTarget}
          loading={deleteLoading}
          error={deleteError}
          onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}

export default Reference;